/**
 * Context for sharing a page's alternate-language URLs with the locale switcher.
 * Set by ContentPage when content loads; consumed by LocaleSelectorModal.
 */

import { createContext, useContext, useEffect } from "react";

/** Maps culture code → localized URL, e.g. { "nb-NO": "/nb-no/tracker-og-wehunt/", "en": "/en/tracker-migration/" } */
export type AlternateUrls = Record<string, string>;

export const AlternateUrlsContext = createContext<AlternateUrls>({});
export const SetAlternateUrlsContext = createContext<
  (urls: AlternateUrls) => void
>(() => {});

export function useAlternateUrls(): AlternateUrls {
  return useContext(AlternateUrlsContext);
}

export function useSetAlternateUrls(): (urls: AlternateUrls) => void {
  return useContext(SetAlternateUrlsContext);
}

/**
 * Publishes a fixed set of alternate URLs to the locale switcher for the lifetime
 * of the calling component, clearing them on unmount. Used by dedicated routes
 * (My Account, Checkout, B2B) that don't fetch CMS content but carry per-culture
 * URLs in the layout payload, so switching language maps to the localized page
 * instead of falling back to the home page.
 */
export function useProvideAlternateUrls(
  alternateUrls: AlternateUrls | undefined
): void {
  const setAlternateUrls = useContext(SetAlternateUrlsContext);
  // Serialize so the effect re-runs only when the actual mapping changes, not on
  // every render that produces a new object identity.
  const key = JSON.stringify(alternateUrls ?? {});

  useEffect(() => {
    setAlternateUrls(alternateUrls ?? {});
    return () => setAlternateUrls({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setAlternateUrls]);
}
